import { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import intl from 'react-intl-universal';
import {
  Card,
  Box,
  Typography,
  TextField,
  CircularProgress,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import Grid from '@mui/material/Grid2';
import FormField from 'components/FormField';
import CachedIcon from '@mui/icons-material/Cached';
import SaveIcon from '@mui/icons-material/Save';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { zIndexValues } from 'utils/styles';
import {
  createTextField,
  CreateDndField,
  createSelectField,
  createNumberField,
  createDateField,
  createImageField,
} from './components';
import logger from 'utils/logger';
import { Buffer } from 'buffer';
import { REPORT_ELEMENT_TYPE } from 'utils/ReportUtils';
import {
  KEY_LABELS,
  LABELS,
} from 'components/Report/Templates/DocloudTemplate/constants';
import { doesUserHaveSeimitsuViewerPrivilege } from 'utils/privilege';
import ConfirmDeleteModal from 'components/Report/components/ConfirmDeleteModal';
import { isDeepEqual } from 'utils/equality';

const log = logger.createLogger('ReportEditor');

const propTypes = {
  reportContent: PropTypes.object.isRequired,
  setReportContent: PropTypes.func.isRequired,
  debugBox: PropTypes.object.isRequired,
  setDebugBox: PropTypes.func.isRequired,
  setReRender: PropTypes.func.isRequired,
  togglePreview: PropTypes.func.isRequired,
  openViewer: PropTypes.func.isRequired,
  shouldAllowOpenViewer: PropTypes.bool.isRequired,
  saveReportContent: PropTypes.func.isRequired,
  keyImagesFromViewer: PropTypes.array,
  setKeyImagesFromViewer: PropTypes.func,
};

const ReportEditor = ({
  reportContent,
  setReportContent,
  debugBox,
  setDebugBox,
  setReRender,
  togglePreview,
  openViewer,
  shouldAllowOpenViewer,
  saveReportContent,
  keyImagesFromViewer,
  setKeyImagesFromViewer,
}) => {
  const initialPlacedElements = reportContent.content
    .filter((field) => field.type === REPORT_ELEMENT_TYPE.DND)
    .reduce((acc, field) => {
      acc[field.id] = field.dndElements || [];
      return acc;
    }, {});

  const [dndPlacedElements, setDndPlacedElements] = useState(
    initialPlacedElements
  );
  const isSeimitsuViewerPrivilege = doesUserHaveSeimitsuViewerPrivilege();
  const [isKeyImageFromViewer, setIsKeyImageFromViewer] = useState(false);
  const [isConfirmDeleteKeyImage, setIsConfirmDeleteKeyImage] = useState(false);
  const [keyImageToDelete, setKeyImageToDelete] = useState(null);
  const [keyImageIndexToDelete, setKeyImageIndexToDelete] = useState(null);

  // For debugging purposes
  const isDebug = false;

  const handleChange = (fieldId) => (event) => {
    const currentField = reportContent.content.find(
      (field) => field.id === fieldId
    );
    if (currentField.type === REPORT_ELEMENT_TYPE.DATE) {
      setReportContent((prevState) => {
        return {
          ...prevState,
          content: prevState.content.map((field) =>
            field.id === fieldId
              ? { ...field, value: event.toISOString() }
              : field
          ),
        };
      });
    } else {
      setReportContent((prevState) => {
        return {
          ...prevState,
          content: prevState.content.map((field) =>
            field.id === fieldId
              ? { ...field, value: event.target.value }
              : field
          ),
        };
      });
    }
  };

  const handleRerenderButtonClick = () => {
    setReRender((prevState) => prevState + 1);
  };

  const handleImages = async (files) => {
    for (var i = 0; i < files.length; i++) {
      let file = files[i];
      const fileName = file.name;
      const fileBuffer = await file.arrayBuffer();

      // Get the current images
      const currentImages = reportContent.content.find(
        (field) => field.label === KEY_LABELS[LABELS.KEY_IMAGES]
      ).value;

      // Add the new image to the current images
      currentImages.push({
        fileName: fileName,
        fileBuffer: Buffer.from(fileBuffer),
      });

      setReportContent((prevState) => {
        return {
          ...prevState,
          content: prevState.content.map((field) =>
            field.label === KEY_LABELS[LABELS.KEY_IMAGES]
              ? { ...field, value: currentImages }
              : field
          ),
        };
      });
    }
  };

  const checkKeyImageFromViewer = (imageToCheck) => {
    const result = keyImagesFromViewer.some((keyImage) =>
      isDeepEqual(keyImage, imageToCheck)
    );
    return result;
  };

  const handleKeyImageDelete = (item, index) => {
    setIsKeyImageFromViewer(checkKeyImageFromViewer(item));
    setKeyImageToDelete(item);
    setKeyImageIndexToDelete(index);
    setIsConfirmDeleteKeyImage(true);
  };

  const deleteKeyImageFromReportData = () => {
    const currentImages = reportContent.content.find(
      (field) => field.label === KEY_LABELS[LABELS.KEY_IMAGES]
    ).value;

    currentImages.splice(keyImageIndexToDelete, 1);

    setReportContent((prevState) => {
      return {
        ...prevState,
        content: prevState.content.map((field) =>
          field.label === KEY_LABELS[LABELS.KEY_IMAGES]
            ? { ...field, value: currentImages }
            : field
        ),
      };
    });
  };

  const removeKeyImageFromReport = () => {
    deleteKeyImageFromReportData();
    setIsConfirmDeleteKeyImage(false);
    setKeyImageToDelete(null);
    setKeyImageIndexToDelete(null);
  };

  const handleRemoveKeyImageFromIssue = () => {
    deleteKeyImageFromReportData();
    setKeyImagesFromViewer(
      keyImagesFromViewer.filter((_, i) => i !== keyImageIndexToDelete)
    );
    // Reset the state
    setIsConfirmDeleteKeyImage(false);
    setKeyImageToDelete(null);
    setKeyImageIndexToDelete(null);
  };

  // For DND fields
  const handleCoordinatesChange = (fieldId, newElements) => {
    setDndPlacedElements((prevState) => {
      return {
        ...prevState,
        [fieldId]: newElements,
      };
    });

    // Need to create a new object inside as dndElements
    setReportContent((prevState) => {
      return {
        ...prevState,
        content: prevState.content.map((field) =>
          field.id === fieldId ? { ...field, dndElements: newElements } : field
        ),
      };
    });
  };

  const addField = (index, field) => {
    //! Do not render the field if it has a hidden flag
    if (field.hidden ? field.hidden : false) {
      return null;
    }

    switch (field.type) {
      case REPORT_ELEMENT_TYPE.TEXT:
        return createTextField(index, field, handleChange);
      case REPORT_ELEMENT_TYPE.DND:
        //! Needs to be a component as there are async operations
        return (
          <CreateDndField
            key={index}
            reportTemplateId={reportContent.id}
            index={index}
            field={field}
            handleCoordinatesChange={handleCoordinatesChange}
            placedElements={dndPlacedElements[field.id]}
          />
        );
      case REPORT_ELEMENT_TYPE.SELECT_OBJECT:
      case REPORT_ELEMENT_TYPE.SELECT_TEXT:
        return createSelectField(index, field, handleChange);
      case REPORT_ELEMENT_TYPE.NUMBER:
        return createNumberField(index, field, handleChange);
      case REPORT_ELEMENT_TYPE.DATE:
        return createDateField(index, field, handleChange);
      case REPORT_ELEMENT_TYPE.IMAGE:
        return createImageField(
          index,
          field,
          handleImages,
          handleKeyImageDelete
        );
      case REPORT_ELEMENT_TYPE.BLANK:
        return null;
      default:
        log.error('Unknown field type', field.type);
        return null;
    }
  };

  //! TODO Add permissions to the buttons
  return (
    <Fragment>
      <Card square={true} sx={{ overflow: 'visible', minHeight: '100%' }}>
        <MDBox py={3} px={3} mt={1} mx={3} height="100%">
          <MDBox pl={1} mb={2} display="center" justifyContent="center">
            <MDTypography variant="h5" data-testid="report_editor_title">
              {intl.get('issue_details_report_preview_text_report_title')}
            </MDTypography>
          </MDBox>
          <Grid>
            <Grid container spacing={2}>
              <Grid size={6}>
                <FormField
                  data-testid="report_editor_filename"
                  variant="outlined"
                  label={intl.get(KEY_LABELS[LABELS.TITLE])}
                  onChange={(e) => {
                    setReportContent((prevState) => {
                      return {
                        ...prevState,
                        savedFileName: e.target.value,
                      };
                    });
                  }}
                  value={reportContent.savedFileName}
                />
              </Grid>

              <Grid container size={6} spacing={1} justifyContent="flex-end">
                <Grid>
                  <IconButton
                    onClick={handleRerenderButtonClick}
                    aria-label={intl.get(
                      'issue_details_report_preview_text_rerender'
                    )}>
                    <Tooltip
                      title={intl.get(
                        'issue_details_report_preview_text_rerender'
                      )}
                      slotProps={{
                        popper: {
                          sx: { zIndex: zIndexValues.iconPopover },
                        },
                      }}>
                      <CachedIcon />
                    </Tooltip>
                  </IconButton>
                </Grid>

                <Grid>
                  <IconButton
                    onClick={saveReportContent}
                    aria-label={intl.get(
                      'issue_details_report_preview_text_save'
                    )}>
                    <Tooltip
                      title={intl.get('issue_details_report_preview_text_save')}
                      slotProps={{
                        popper: {
                          sx: { zIndex: zIndexValues.iconPopover },
                        },
                      }}>
                      <SaveIcon />
                    </Tooltip>
                  </IconButton>
                </Grid>
                {shouldAllowOpenViewer && isSeimitsuViewerPrivilege && (
                  <Grid>
                    <IconButton
                      onClick={openViewer}
                      aria-label={intl.get(
                        'issue_details_report_preview_text_open_viewer'
                      )}>
                      <Tooltip
                        title={intl.get(
                          'issue_details_report_preview_text_open_viewer'
                        )}
                        slotProps={{
                          popper: {
                            sx: { zIndex: zIndexValues.iconPopover },
                          },
                        }}>
                        <VisibilityIcon />
                      </Tooltip>
                    </IconButton>
                  </Grid>
                )}

                <Grid>
                  <IconButton
                    onClick={togglePreview}
                    aria-label={intl.get(
                      'issue_details_report_preview_text_toggle_preview'
                    )}>
                    <Tooltip
                      title={intl.get(
                        'issue_details_report_preview_text_toggle_preview'
                      )}
                      slotProps={{
                        popper: {
                          sx: { zIndex: zIndexValues.iconPopover },
                        },
                      }}>
                      <FindInPageIcon />
                    </Tooltip>
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>

            {/* Debugging purposes */}
            {isDebug && (
              <Grid size={12}>
                <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    Position and Size Form
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={1}>
                      <Typography variant="body2" textAlign="center">
                        X:
                      </Typography>
                    </Grid>
                    <Grid size={2}>
                      <TextField
                        type="number"
                        label="X Position"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={debugBox.x}
                        onChange={(e) =>
                          setDebugBox({
                            ...debugBox,
                            x: Number(e.target.value) || 0,
                          })
                        }
                      />
                    </Grid>
                    <Grid size={1}>
                      <Typography variant="body2" textAlign="center">
                        Y:
                      </Typography>
                    </Grid>
                    <Grid size={2}>
                      <TextField
                        type="number"
                        label="Y Position"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={debugBox.y}
                        onChange={(e) =>
                          setDebugBox({
                            ...debugBox,
                            y: Number(e.target.value) || 0,
                          })
                        }
                      />
                    </Grid>
                    <Grid size={1}>
                      <Typography variant="body2" textAlign="center">
                        W:
                      </Typography>
                    </Grid>
                    <Grid size={2}>
                      <TextField
                        type="number"
                        label="Width"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={debugBox.width}
                        onChange={(e) =>
                          setDebugBox({
                            ...debugBox,
                            width: Number(e.target.value) || 0,
                          })
                        }
                      />
                    </Grid>
                    <Grid size={1}>
                      <Typography variant="body2" textAlign="center">
                        H:
                      </Typography>
                    </Grid>
                    <Grid size={2}>
                      <TextField
                        type="number"
                        label="Height"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={debugBox.height}
                        onChange={(e) =>
                          setDebugBox({
                            ...debugBox,
                            height: Number(e.target.value) || 0,
                          })
                        }
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            )}

            <Grid
              container
              rowSpacing={2}
              columnSpacing={3}
              style={{ marginTop: 15 }}>
              {reportContent.content.map((field, index) =>
                addField(index, field)
              )}
            </Grid>
          </Grid>
        </MDBox>
      </Card>

      <ConfirmDeleteModal
        isOpen={isConfirmDeleteKeyImage}
        isKeyImageFromViewer={isKeyImageFromViewer}
        setOpen={setIsConfirmDeleteKeyImage}
        handleRemoveKeyImage={removeKeyImageFromReport}
        handleRemoveKeyImageFromIssue={handleRemoveKeyImageFromIssue}
        keyImage={keyImageToDelete}
      />
    </Fragment>
  );
};

ReportEditor.propTypes = propTypes;

export default ReportEditor;
